/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { cn } from '@/lib/utils';
import withAuthenticator from '@/hoc/withAuthenticator/withAuthenticator';

import FiltersBar, {
  CLEAN_FILTERS,
} from '@/components/conversation-insights/filters-bar';
import Insights from '@/components/conversation-insights/insights';
import Leaderboard from '@/components/conversation-insights/leaderboard';
import { MultiSelectOption } from '@/components/ui/multi-select-button';
import { useConversationsInsights } from '@/hooks/useConversationsInsights';
import type { RootState } from '@/store/reducers';
import { API_BASE_URL, APP_BASE_URL, ROUTES } from '@/utils/constants';
import { getCRMStatus } from '@/handlers/handlers';
import ConversationsService from '@/services/conversations/conversations.service';
import OrganisationService from '@/services/organisation/organisation.service';
import CustomerService from '@/services/customer/customer.service';
import { debounce } from '@material-ui/core';
import HttpClient from '@/utils/http-client';

import LeaderboardSheet from '@/components/conversation-insights/leaderboard-sheet';
import { useSidebar } from '@/components/ui/sidebar';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import ZipInsights from '@/components/conversation-insights/zip-insights';
import { Skeleton } from '@/components/ui/skeleton';
import CopyIcon from '../../public/icons/new-dashboard/copy-left.svg';
import ToggleExpandIcon from '../../public/icons/new-dashboard/arrow-expand-04.svg';
import EmptyZipInsights from '../../public/icons/new-dashboard/empty-zip-insights.svg'

export const intentOptions: MultiSelectOption[] = [
  { value: 'HIGH_INTENT', label: 'High' },
  { value: 'MODERATE', label: 'Moderate' },
  { value: 'LOW_INTENT', label: 'Low' },
  { value: 'NEUTRAL', label: 'Neutral' },
];

export type RawAgent = {
  assigned_user_id: number;
  assigned_user_first_name: string;
  assigned_user_last_name: string | null;
  assigned_user_avatar_url: string | null;
  assigned_user_email: string;
};

export function buildAgentOptions(list: RawAgent[]): MultiSelectOption[] {
  return list.map(a => {
    const fullName = `${a.assigned_user_first_name} ${
      a.assigned_user_last_name ?? ''
    }`.trim();
    return {
      value: String(a.assigned_user_id),
      label: fullName || a.assigned_user_email,
      avatarUrl: a.assigned_user_avatar_url ?? undefined,
    };
  });
}

export function buildQualificationQuestions(list: any[]) {
  return list.map(q => ({
    capture_id: q?._id,
    qualification_question: q.qualification_question,
    /* API uses "bool" – that's already allowed by the union type.
       If the backend ever sends "boolean", keep it as-is.            */
    return_type: q.return_type as 'string' | 'integer' | 'bool' | 'boolean',
    capture_output: [], // start empty; user will fill this
  }));
}

export function buildStatusOptions(list: any[]): MultiSelectOption[] {
  return (
    list
      // keep only “active” ones and de-duplicate by id
      .filter(s => s.status === 'active')
      .filter((s, i, arr) => arr.findIndex(t => t.id === s.id) === i)
      // sort alphabetically
      .sort((a, b) => a.name.localeCompare(b.name))
      // map to MultiSelectOption
      .map(s => ({
        value: String(s.id),
        label: s.name.replace(/_/g, ' '),
      }))
  );
}

export function buildCustomerOptions(list: any[]): MultiSelectOption[] {
  return list.map(c => {
    const customerName = c?.contact_name ?? c?.customer_name;
    const customerEmail = c?.contact_email;
    const customerPhone = c?.contact_phone;

    return {
      value: String(c.customer_id),
      label: [customerName, customerEmail, customerPhone]
        .filter(Boolean)
        ?.join(' | '),
      avatarUrl: c.avatar_url ?? undefined,
    };
  });
}

export function buildObjectionOptions(list: string[]): MultiSelectOption[] {
  return list.filter(Boolean).map(text => ({
    value: text,
    label: text,
    avatarUrl: undefined,
  }));
}

export function buildPitchGapOptions(list: any[]) {
  return list.map(listItem => ({
    value: listItem?.id,
    label: listItem?.label,
    avatarUrl: undefined,
  }));
}

export function pruneEmptyArrays(filters = {}) {
  return Object.fromEntries(
    Object.entries(filters).filter(
      ([, v]) => !(Array.isArray(v) && v.length === 0),
    ),
  );
}

/* ────────────────────────────────────────────────────────────
 *  Component
 * ──────────────────────────────────────────────────────────── */
function ConversationInsights() {
  const { isMobile } = useSidebar();
  const { user, isAdmin, isManager } = useSelector((s: RootState) => s.common);

  const showEmailPhone = Boolean(
    user?.organisation?.feature_flags?.show_PII_report,
  );

  const selectedWorkspace = useSelector(
    (state: RootState) => state.common.selectedWorkspace[0],
  );

  const [agents, setAgents] = useState([]);
  const [qualificationQuestions, setQualificationQuestions] = useState([]);
  const [status, setStatus] = useState([]);
  const [customerOptions, setCustomerOptions] = React.useState([]);
  const [customersLoading, setCustomersLoading] = React.useState(false);

  const [page, setPage] = useState(1);
  const limit = 10;
  // const PAGE_SIZE = 6;
  // const sortField = "last_conversation";
  // const sortOrder: "asc" | "desc" = "desc";

  const debounceSuggestionsWorkspace = debounce((newValue: string) => {
    (async () => {
      try {
        setCustomersLoading(true);
        const workspace_id = String(selectedWorkspace?.id);
        const method = CustomerService.getSearchSuggestionsWorkspace;

        const { data } = await method(
          newValue,
          10,
          workspace_id,
          !isAdmin && isManager ? user.id : null,
        );

        const customerList = data?.data?.customerList?.map(i => ({
          customer_id: i?.customer_id,
          customer_name: i?.customer_name,
          contact_name: i?.contact_name,
          contact_email: i?.contact_email,
          contact_phone: i?.contact_phone,
        }));

        setCustomerOptions(buildCustomerOptions(customerList));
        setCustomersLoading(false);
      } catch (error) {
        setCustomersLoading(false);
        console.log({ error });
      }
    })();
  }, 400);

  const [filters, setFilters] = useState(CLEAN_FILTERS);
  const [previousAppliedFilters, setPreviousAppliedFilters] = useState(filters);
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  useEffect(() => {
    const {
      search_date: baseSearchDate,
      customer_id: baseCustomerId,
      conversation_owner_ids: baseConversationOwners,
      crm_status_ids: baseCrmStatus,
      ...baseCleanFilters
    } = CLEAN_FILTERS;
    const {
      search_date,
      customer_id,
      conversation_owner_ids,
      crm_status_ids,
      ...currentFilters
    } = filters;

    const isDifferent =
      JSON.stringify(baseCleanFilters) !== JSON.stringify(currentFilters);
    if (isDifferent) {
      setShowMoreFilters(isDifferent);
    }
  }, [filters]);

  const handleDownloadReport = async () => {
    try {
      const payload = pruneEmptyArrays(filters);
      const response = await HttpClient.post(
        `${API_BASE_URL}/v1/customer/insights/workspace/${selectedWorkspace?.id}/reports`,
        { filters: payload },
      );
      console.log(response);
    } catch (error) {
      console.log({ error });
    }
  };

  const fetchCrmStatus = async () => {
    try {
      const response = await getCRMStatus();
      const allStatus = response;
      setStatus(buildStatusOptions(allStatus));
    } catch (error) {
      console.log({ error });
    }
  };

  const fetchAllAssignes = async () => {
    try {
      const response = await ConversationsService.getAllAssigneeUsers(
        selectedWorkspace?.id,
      );
      const assigneeUsers = response?.data?.data;

      setAgents(buildAgentOptions(assigneeUsers));
    } catch (error) {
      console.log({ error });
    }
  };

  const fetchQUalificationQuestions = async () => {
    try {
      const workspace_id = selectedWorkspace?.id;
      const response =
        await OrganisationService.getWorkspaceQualificationQuestions(
          workspace_id,
        );
      const questions = response?.data?.data
        .filter(item => !item?.deleted_at && item.is_active)
        .map(item => ({ ...item, filter: false }));

      setQualificationQuestions(buildQualificationQuestions(questions));
    } catch (error) {
      console.log({ error });
    }
  };

  /* hook: fetch data */
  const {
    objections,
    overview,
    intentSpread,
    keyInsights,
    zipInsights,
    customers,
    agentPitchGaps,
    loadingObjections,
    loadingOverview,
    loadingIntentSpread,
    loadingKeyInsights,
    loadingCustomers,
    loadingAgentPitchGaps,
    fetchObjections,
    fetchOverview,
    fetchIntentSpread,
    fetchKeyInsights,
    fetchCustomers,
    fetchAgentPitchGaps,
  } = useConversationsInsights(
    selectedWorkspace?.id,
    page,
    limit,
    pruneEmptyArrays(filters),
    null,
  );

  useEffect(() => {
    fetchObjections();
    fetchAgentPitchGaps();
    fetchOverview();
    fetchIntentSpread();
    fetchKeyInsights();
    fetchCustomers();

    // get filters data
    fetchCrmStatus();
    fetchAllAssignes();
    fetchQUalificationQuestions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (cleanPayload?: any) => {
    setPreviousAppliedFilters(cleanPayload ?? filters);
    const payload = cleanPayload
      ? pruneEmptyArrays(cleanPayload)
      : pruneEmptyArrays(filters);
    fetchOverview(payload);
    fetchIntentSpread(payload);
    fetchKeyInsights(payload);
    fetchCustomers(payload);
  };

  const handlePageChange = pageNumber => {
    if (pageNumber) {
      const payload = pruneEmptyArrays(filters);
      fetchCustomers(payload, pageNumber);
      setPage(pageNumber);
    }
  };

  const handleIntentClick = (intent: string) => {
    const updatedFilters = { ...filters, intents: [intent] };
    setFilters(updatedFilters);
    setPreviousAppliedFilters(updatedFilters);
    const payload = pruneEmptyArrays(updatedFilters);
    fetchOverview(payload);
    fetchIntentSpread(payload);
    fetchKeyInsights(payload);
    fetchCustomers(payload);
  };

  const handleIntentRemove = (intent: string) => {
    const currentIntents = filters.intents ?? [];
    const updatedIntents = currentIntents.filter(i => i !== intent);
    const updatedFilters = {
      ...filters,
      intents: updatedIntents,
    };

    setFilters(updatedFilters);
    setPreviousAppliedFilters(updatedFilters);
    const payload = pruneEmptyArrays(updatedFilters);
    fetchOverview(payload);
    fetchIntentSpread(payload);
    fetchKeyInsights(payload);
    fetchCustomers(payload);
  };

  const handleLeaderboardUserClick = (id: any) => {
    const updatedFilters = { ...filters, conversation_owner_ids: [id] };
    setFilters(updatedFilters);
    setPreviousAppliedFilters(updatedFilters);
    const payload = pruneEmptyArrays(updatedFilters);
    fetchOverview(payload);
    fetchIntentSpread(payload);
    fetchKeyInsights(payload);
    fetchCustomers(payload);
  };

  const handleLeaderboardUserRemove = (id: any) => {
    const currentOwners = filters.conversation_owner_ids ?? [];
    const updatedOwners = currentOwners.filter(ownerId => ownerId !== id);
    const updatedFilters = {
      ...filters,
      conversation_owner_ids: updatedOwners,
    };
    setFilters(updatedFilters);
    setPreviousAppliedFilters(updatedFilters);
    const payload = pruneEmptyArrays(updatedFilters);
    fetchOverview(payload);
    fetchIntentSpread(payload);
    fetchKeyInsights(payload);
    fetchCustomers(payload);
  };

  const handleConcernsPieSlice = (concern: string) => {
    const updatedFilters = { ...filters, concern_categories: [concern] };
    setFilters(updatedFilters);
    setPreviousAppliedFilters(updatedFilters);
    const payload = pruneEmptyArrays(updatedFilters);
    fetchOverview(payload);
    fetchIntentSpread(payload);
    fetchKeyInsights(payload);
    fetchCustomers(payload);
  };

  const handleAgentGapsPieSlice = (agentGap: string) => {
    const agentGapId = agentPitchGaps?.find(
      eachGap => agentGap === eachGap.label,
    )?.id;
    const updatedFilters = { ...filters, agent_pitch_gaps: [agentGapId] };
    setFilters(updatedFilters);
    setPreviousAppliedFilters(updatedFilters);
    const payload = pruneEmptyArrays(updatedFilters);
    fetchOverview(payload);
    fetchIntentSpread(payload);
    fetchKeyInsights(payload);
    fetchCustomers(payload);
  };

  const handleCopyZipInsights = () => {
    if (!zipInsights?.length) return;
  
    const coreText = zipInsights
      .map(eachZipInsight => {
        let header = '';
        if (Array.isArray(agents) && agents.length > 0) {
          const currentAgent = agents.find(
            eachAgent =>
              Number(eachAgent?.value) === Number(eachZipInsight?.user_id)
          );
          if (currentAgent?.label) {
            header = currentAgent.label
          }
        }

        const lines = Array.isArray(eachZipInsight.insights)
          ? eachZipInsight.insights.map(i => ` • ${i.sentiment}: ${i.insight}`)
          : [];

        return header ? [header, ...lines].join('\n') : lines.join('\n');
      })
      .filter(textBlock => textBlock) // drop empty blocks
      .join('\n\n');
  
    const footer = [
      '',
      '',
      'See detailed dashboard on Zip',
      `${APP_BASE_URL}/conversation-insights`,
    ].join('\n');

    const finalText = coreText + footer;


    navigator.clipboard
      .writeText(finalText)
      .then(() => {
        // alert('Insights copied to clipboard!');
      })
      .catch(err => {
        console.error('Copy failed', err);
      });
  };

  const [openZipInsights, setOpenZipInsights] = useState(true);
  const [openLeaderboard, setOpenLeaderboard] = useState(true);

  return (
    <div className={cn('flex h-full')}>
      <ResizablePanelGroup direction="horizontal" className="max-w-full">
        {/* ───── Left main area ───── */}
        <ResizablePanel className="h-full min-h-0">
          <div
            className={cn(
              'flex flex-col h-full min-h-0 overflow-y-auto',
              // width of the scrollbar in WebKit
              '[&::-webkit-scrollbar]:w-2',
              // track color in WebKit
              '[&::-webkit-scrollbar-track]:bg-[#F4F4F5]',
              // thumb color + rounded in WebKit
              '[&::-webkit-scrollbar-thumb]:bg-[#09090B]',
              '[&::-webkit-scrollbar-thumb]:rounded-full',
              // Firefox: thin + color (thumb then track)
              '[scrollbar-width:thin]',
              '[scrollbar-color:#09090B_#F4F4F5]',
            )}
          >
            {/* Header + filters */}
            <div
              className={cn(
                'sticky top-0 z-10 bg-white border-b border-[#E4E4E7] space-y-2 p-4',
              )}
            >
              <div
                className={cn('flex items-end', {
                  'justify-between': isMobile,
                })}
              >
                <h1 className="text-xl font-bold">Conversations Insights</h1>
                {isMobile && (
                  <LeaderboardSheet
                    workspaceId={selectedWorkspace.id}
                    organisationId={user?.organisation_id}
                    loadingKeyInsights={loadingKeyInsights}
                    zipInsights={zipInsights}
                    agents={agents}
                    previousAppliedFilters={previousAppliedFilters}
                    openZipInsights={openZipInsights}
                    handleLeaderboardUserClick={handleLeaderboardUserClick}
                    handleLeaderboardUserRemove={handleLeaderboardUserRemove}
                    handleCopyZipInsights={handleCopyZipInsights}
                    openLeaderboard={openLeaderboard}
                    setOpenLeaderboard={setOpenLeaderboard}
                    setOpenZipInsights={setOpenZipInsights}
                  />
                )}
              </div>
              <p className="text-sm text-muted-foreground !mt-0">
                View all conversations insights related to your team.
              </p>

              <FiltersBar
                customersLoading={customersLoading}
                debounceSuggestionsWorkspace={debounceSuggestionsWorkspace}
                customerOptions={customerOptions}
                agentOptions={agents}
                statusOptions={status}
                concernOptions={buildObjectionOptions(objections)}
                pitchGapOptions={buildPitchGapOptions(agentPitchGaps)}
                intentOptions={intentOptions}
                qualificationQuestions={qualificationQuestions}
                onChange={setFilters}
                globalFilters={filters}
                onSubmit={handleSubmit}
                previousAppliedFilters={previousAppliedFilters}
                loadingOverview={loadingOverview}
                loadingIntentSpread={loadingIntentSpread}
                loadingKeyInsights={loadingKeyInsights}
                filters={filters}
                setFilters={setFilters}
                showMoreFilters={showMoreFilters}
                setShowMoreFilters={setShowMoreFilters}
                // setPreviousAppliedFilters={setPreviousAppliedFilters}
              />
            </div>

            {/* Metrics + tables */}
            <Insights
              overview={overview}
              intentSpread={intentSpread}
              customers={customers}
              loadingOverview={loadingOverview}
              loadingIntentSpread={loadingIntentSpread}
              loadingCustomers={loadingCustomers}
              loadingKeyInsights={loadingKeyInsights}
              loadingObjections={loadingObjections}
              keyInsights={keyInsights}
              objections={objections}
              handleDownloadReport={handleDownloadReport}
              handlePageChange={handlePageChange}
              page={page}
              handleIntentClick={handleIntentClick}
              handleIntentRemove={handleIntentRemove}
              insightsFilters={previousAppliedFilters}
              showEmailPhone={showEmailPhone}
              handleConcernsPieSlice={handleConcernsPieSlice}
              handleAgentGapsPieSlice={handleAgentGapsPieSlice}
            />
          </div>
        </ResizablePanel>

        {!isMobile && (
          <>
            <ResizableHandle />

            {/* ───── Right leaderboard panel ───── */}
            <ResizablePanel className="basis-[312px] min-w-[312px] max-w-[400px]">
            <div className="flex flex-col h-full">
              <section className={cn("px-4 pt-5 max-h-[50%]")}>
                <div className="flex items-center justify-between">
                  <h2 className="font-bold break-words">Zip Insights</h2>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="hover:bg-gray-100 h-6 w-6 p-1 flex items-center justify-center rounded-lg"
                      onClick={handleCopyZipInsights}
                    >
                      <CopyIcon />
                    </button>
                    <button
                      type="button"
                      className="hover:bg-gray-100 h-6 w-6 p-1 flex items-center justify-center rounded-lg"
                      onClick={() => setOpenZipInsights((prev) => !prev)}
                    >
                      <ToggleExpandIcon />
                    </button>
                  </div>
                </div>

                <p className="mt-1 text-sm text-paragraph">
                  AI-powered insights from your sales activity
                </p>

                {openZipInsights ? (
                  loadingKeyInsights ? (
                    <div className="my-4 h-full w-full text-center">
                      <Skeleton className="h-[40px] rounded-xl bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7]" />
                      <Skeleton className="h-[40px] rounded-xl bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7] mt-4" />
                      <Skeleton className="h-[40px] rounded-xl bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7] mt-4" />
                    </div>
                  ) : zipInsights?.length > 0 ? (
                    <ZipInsights zipInsights={zipInsights} agents={agents} />
                  ) : (
                    <div className="h-72 flex flex-col items-center justify-center">
                      <EmptyZipInsights />
                      <p className="text-center max-w-64 text-sm text-paragraph">
                        Looks like there are no insights available at the moment.
                      </p>
                    </div>
                  )
                ) : (
                  <div />
                )}
              </section>

              <section className="px-4 pt-4 flex-1 overflow-y-auto border-t border-gray-200 bg-white">
                <div className="flex items-center justify-between">
                  <p className="font-bold break-words">Leaderboard</p>
                  <div />
                  {/* <button
                    type="button"
                    className="hover:bg-gray-100 h-6 w-6 p-1 flex items-center justify-center rounded-lg mr-4 opacity-0" 
                    // onClick={() => setOpenLeaderboard(prev => !prev)}
                  >
                    <ToggleExpandIcon />
                  </button> */}
                </div>

                <p className="mt-1 text-sm text-paragraph">
                  Sort your team by the key metrics
                </p>

                {openLeaderboard && (
                  <Leaderboard
                    workspaceId={selectedWorkspace.id}
                    organisationId={user?.organisation_id}
                    insightsFilters={previousAppliedFilters}
                    openZipInsights={openZipInsights}
                    handleLeaderboardUserClick={handleLeaderboardUserClick}
                    handleLeaderboardUserRemove={handleLeaderboardUserRemove}
                  />
                )}
              </section>
            </div>
            </ResizablePanel>
          </>
        )}
      </ResizablePanelGroup>
    </div>
  );
}

/* wrap with your HOC */
export default withAuthenticator(ConversationInsights, {
  redirectPath: `/auth/${ROUTES.AUTH_TYPES.LOGIN}`,
  onlyAdmin: true,
});
